import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useLang } from "@/context/LangContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_VIDEO = "/hero-dj.mp4";
const HERO_VIDEO_WEBM = "/hero-dj.webm";

const FALLBACK_POSTER = "https://images.unsplash.com/photo-1759855714726-2b5da0aa20c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

function MaskLine({ children, delay = 0, className = "" }) {
  return (
    <span className="block overflow-hidden pb-[0.08em]">
      <motion.span
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ delay, duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
        className={`inline-block ${className}`}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { t } = useLang();
  const [poster, setPoster] = useState(FALLBACK_POSTER);
  const [caption, setCaption] = useState({ title: "DJ BOOTH TRAILER", subtitle: "Une expérience événementielle unique au Québec" });
  const [customVideo, setCustomVideo] = useState(null);
  const [scrollY, setScrollY] = useState(0);
  const videoRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/content/hero_slides`).then(r => {
      if (r.data?.length) {
        const first = r.data[0];
        if (first.image) setPoster(first.image);
        setCaption({ title: first.title || "DJ BOOTH TRAILER", subtitle: first.subtitle || "Une expérience événementielle unique au Québec" });
      }
    }).catch(e => console.error("Hero slides error:", e));
    axios.get(`${API}/content/site_info`).then(r => {
      if (r.data?.hero_video_url) setCustomVideo(r.data.hero_video_url);
    }).catch(e => console.error("Site info error:", e));
  }, []);

  // Force autoplay reliably (also re-plays when a custom video is loaded)
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    try { v.load(); } catch (e) { /* noop */ }
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }, [customVideo]);

  // Parallax scroll
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="accueil" className="relative min-h-[100vh] flex items-center overflow-hidden" data-testid="hero-section">
      {/* Persistent looping video background */}
      <div className="absolute inset-0" style={{ transform: `translateY(${scrollY * 0.15}px)` }}>
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={poster}
          className="w-full h-full object-cover"
          data-testid="hero-video"
        >
          {customVideo ? (
            <source src={customVideo} type={customVideo.toLowerCase().endsWith(".webm") ? "video/webm" : "video/mp4"} />
          ) : (
            <>
              <source src={HERO_VIDEO_WEBM} type="video/webm" />
              <source src={HERO_VIDEO} type="video/mp4" />
            </>
          )}
        </video>
      </div>

      {/* Overlays for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/40 to-black/20" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-transparent" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#E60000]/10 rounded-full blur-[150px]" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-12 w-full pt-28 sm:pt-32 pb-20 sm:pb-24">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mb-6"
          >
            <span className="inline-block bg-[#E60000] text-white text-xs font-bold uppercase tracking-[0.3em] px-4 py-2 mb-3">
              {caption.title}
            </span>
            <p className="font-body text-lg sm:text-xl text-white/80 font-light" style={{ textShadow: "0 2px 10px rgba(0,0,0,0.9)" }}>
              {caption.subtitle}
            </p>
          </motion.div>

          <h1 className="font-impact text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl tracking-[0.01em] uppercase text-white leading-[0.92] mb-5" style={{ textShadow: "0 2px 20px rgba(0,0,0,1), 0 4px 50px rgba(0,0,0,0.8)" }} data-testid="hero-title">
            <MaskLine delay={0.35}>{t.hero.title_line1}</MaskLine>
            <MaskLine delay={0.5}>{t.hero.title_line2}</MaskLine>
            <MaskLine delay={0.65} className="text-[#E60000]">{t.hero.title_line3}</MaskLine>
          </h1>

          <p className="font-body text-sm sm:text-base md:text-lg text-white/80 leading-relaxed max-w-xl mb-8 sm:mb-10 font-light" style={{ textShadow: "0 2px 10px rgba(0,0,0,0.9)" }}>{t.hero.subtitle}</p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <button onClick={() => scrollTo("#soumission")} className="bg-[#E60000] hover:bg-[#FF3333] text-white text-xs sm:text-sm font-bold uppercase tracking-[0.2em] px-6 sm:px-10 py-4 sm:py-5 transition-all duration-300 hover:shadow-2xl hover:shadow-red-900/50 hover:translate-y-[-2px]" data-testid="hero-cta-reserve">
              {t.hero.cta_reserve}
            </button>
            <a href="tel:+15144330846" className="border-2 border-white/20 hover:border-[#E60000] text-white text-xs sm:text-sm font-bold uppercase tracking-[0.2em] px-6 sm:px-10 py-4 sm:py-5 transition-all duration-300 text-center hover:bg-[#E60000]/10" data-testid="hero-cta-call">
              {t.hero.cta_call}
            </a>
          </div>
        </motion.div>
      </div>

      <motion.div className="absolute bottom-16 right-8 hidden md:flex flex-col items-center gap-2" animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
        <ChevronDown className="text-[#E60000] w-5 h-5" />
      </motion.div>
    </section>
  );
}
